/**
 * [#663] Tenant Lead Portal — typed browser reads of the REAL portal surface.
 *
 * Every function here calls a router that already exists and is already
 * mounted under `/api/v1/portal` (src/portal/routes/index.ts) behind the
 * server-side grant chain. Nothing is stubbed, nothing is invented: if a page
 * area is not granted, the underlying request comes back 403
 * PORTAL_ACCESS_DENIED and the caller renders the designed denied state.
 *
 * Row shapes mirror the routers' actual SELECT projections (flat `status` +
 * stage timestamps), not the discriminated-union domain types in
 * src/portal/types.ts — those model the server's internal state, while the
 * wire carries the columns. Keeping the two distinct is deliberate.
 *
 * NOTE: consumer PII never travels through these reads. Portal records
 * reference leads by `leadRef` only; any future surface that carries a
 * consumer value must render it through src/popi mask() with a PIN-gated,
 * audited reveal.
 */

/* eslint no-undef: "off" -- `AbortSignal` is a DOM lib TYPE used only in
   annotations here; the core no-undef rule is not TS-aware (same reason
   services/apiClient.ts disables it per line). */

import { apiClient, ApiError } from "../apiClient";
import type { GrantMatrix } from "../../portal/permissions/pageAreas";
import type {
  ComplaintStatus,
  DeliveryAcceptanceStatus,
  LeadRequestStatus,
  OptoutReturnStatus,
  ResultsReportStatus,
  TenantPortalConfig,
} from "../../portal/types";

/** GET /portal/me — tenant identity + the caller tenant's grant matrix. */
export interface PortalMe {
  readonly tenantId: string;
  readonly tenantName: string | null;
  readonly grants: GrantMatrix;
}

export interface PortalLeadRequestRow {
  readonly id: string;
  readonly campaign: string;
  readonly productClass: string;
  readonly activeAgentCount: number;
  readonly requestedVolume: number;
  readonly status: LeadRequestStatus;
  readonly submittedAt: string | null;
  readonly approvedAt: string | null;
  readonly sourcingStartedAt: string | null;
  readonly deliveredAt: string | null;
  readonly createdAt: string;
}

export interface PortalDeliveryRow {
  readonly id: string;
  readonly requestId: string;
  readonly batchRef: string;
  readonly recordCount: number;
  readonly acceptanceStatus: DeliveryAcceptanceStatus;
  readonly acceptanceDeadline: string | null;
  readonly createdAt: string;
}

export interface PortalResultsReportRow {
  readonly id: string;
  readonly period: string;
  readonly status: ResultsReportStatus;
  readonly submittedAt: string | null;
  readonly confirmedAt: string | null;
  readonly createdAt: string;
}

export interface PortalOptoutReturnRow {
  readonly id: string;
  readonly deliveryId: string;
  readonly leadRef: string;
  readonly registerName: string;
  readonly status: OptoutReturnStatus;
  readonly createdAt: string;
}

export interface PortalComplaintRow {
  readonly id: string;
  readonly leadRef: string;
  readonly summary: string;
  readonly status: ComplaintStatus;
  readonly receivedAt: string;
  readonly createdAt: string;
}

const asArray = <T>(value: unknown): readonly T[] =>
  Array.isArray(value) ? (value as T[]) : [];

export function fetchPortalMe(signal?: AbortSignal): Promise<PortalMe> {
  return apiClient.get("/portal/me", { signal }) as Promise<PortalMe>;
}

export function fetchPortalConfig(
  signal?: AbortSignal,
): Promise<TenantPortalConfig> {
  return apiClient.get("/portal/config", {
    signal,
  }) as Promise<TenantPortalConfig>;
}

export async function fetchLeadRequests(
  signal?: AbortSignal,
): Promise<readonly PortalLeadRequestRow[]> {
  return asArray<PortalLeadRequestRow>(
    await apiClient.get("/portal/lead-requests", { signal }),
  );
}

export async function fetchDeliveries(
  signal?: AbortSignal,
): Promise<readonly PortalDeliveryRow[]> {
  return asArray<PortalDeliveryRow>(
    await apiClient.get("/portal/deliveries", { signal }),
  );
}

export async function fetchResultsReports(
  signal?: AbortSignal,
): Promise<readonly PortalResultsReportRow[]> {
  return asArray<PortalResultsReportRow>(
    await apiClient.get("/portal/results-reports", { signal }),
  );
}

export async function fetchOptoutReturns(
  signal?: AbortSignal,
): Promise<readonly PortalOptoutReturnRow[]> {
  return asArray<PortalOptoutReturnRow>(
    await apiClient.get("/portal/optout-returns", { signal }),
  );
}

export async function fetchComplaints(
  signal?: AbortSignal,
): Promise<readonly PortalComplaintRow[]> {
  return asArray<PortalComplaintRow>(
    await apiClient.get("/portal/complaints", { signal }),
  );
}

/**
 * [#698] WHY the server refused. The two reasons need different user actions,
 * so the client must not flatten them:
 *   role_denied  — the caller's ROLE is not one of the six ratified NU Core
 *                  roles (`normalizeRole` failed closed). A grant would not
 *                  help; the fix is a role change.
 *   grant_denied — the tenant simply holds no grant for the page area.
 * Mirrors the `reason` field of the PORTAL_ACCESS_DENIED envelope.
 */
export type PortalDeniedReason = "role_denied" | "grant_denied";

/** True when a failure is the server's designed portal denial (403). */
export function isPortalAccessDenied(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * The refusal's own `reason`, or null when the error is not a portal denial /
 * carries no reason. Never guessed: an envelope without a recognised reason
 * yields null and the caller keeps its own default rather than inventing one.
 */
export function portalDeniedReason(error: unknown): PortalDeniedReason | null {
  if (!isPortalAccessDenied(error)) return null;
  const body = (error as ApiError).body as { reason?: unknown } | null;
  const reason = body?.reason;
  return reason === "role_denied" || reason === "grant_denied" ? reason : null;
}
